import { useState } from 'react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useUpdateList } from '@/entities/list'
import type { CrmListRow, CampaignShortItem } from '@intone/shared/types'

interface DialogChangeCampaignProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  list: CrmListRow | null
  campaigns: CampaignShortItem[]
}

export function DialogChangeCampaign({
  open,
  onOpenChange,
  list,
  campaigns,
}: DialogChangeCampaignProps) {
  const updateList = useUpdateList()
  const [campaignId, setCampaignId] = useState<number | null>(list?.campaign_id ?? null)

  // Reset campaign when list changes
  if (list && list.campaign_id !== campaignId && campaignId === null) {
    setCampaignId(list.campaign_id)
  }

  const handleSubmit = async () => {
    if (!list || !campaignId) return

    try {
      await updateList.mutateAsync({
        listId: list.id,
        data: { campaign_id: campaignId },
      })
      toast.success('Campaign updated')
      onOpenChange(false)
    } catch (error) {
      toast.error('Failed to update campaign')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Change Campaign</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-body text-gray-600">
            Move list &quot;{list?.name}&quot; to a different campaign.
          </p>

          <div className="space-y-2">
            <Label>Campaign</Label>
            <Select
              value={campaignId ? String(campaignId) : undefined}
              onValueChange={(v) => setCampaignId(parseInt(v, 10))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a campaign" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={updateList.isPending || !campaignId}>
            {updateList.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}